import { getCalculatorProductsPrice } from '~/services/calculator.service'
import type {
    ICalculatorRawSetting,
    ICalculatorSelectedValues,
    ICalculatorSetting,
    ICalculatorSettingUpdaters,
    IFieldTitlesBySlug,
} from '~/types/calculator.types'

export const radioguideCalculatorRawSetting: ICalculatorRawSetting = {
    fields: [
        {
            title: 'Приемники',
            slug: 'transmitters_count',
            max_count: 1000,
            default_disabled: false,
            default_hidden: false,
        },
        {
            title: 'Микрофон говорящего<br/>(головной с передатчиком)',
            slug: 'microphones_count',
            max_count: 10,
            default_disabled: false,
            default_hidden: false,
        },
        {
            title: 'Количество зарядок',
            slug: 'charging_batteries_count',
            max_count: 100,
            default_disabled: false,
            default_hidden: false,
        },
    ],
    default_values: {
        transmitters_count: 14,
        headphones: 'p303',
        microphones_count: 1,
        charging_batteries: 'c215pr',
        charging_batteries_count: 1,
    },
    fields_modal: [
        {
            type: 'counter',
            slug: 'transmitters_count',
            prevent_hidden: false,
        },
        {
            type: 'select',
            slug: 'headphones',
            prevent_hidden: false,
        },
        {
            type: 'counter',
            slug: 'microphones_count',
            prevent_hidden: false,
        },
        {
            type: 'select',
            slug: 'charging_batteries',
            prevent_hidden: false,
        },
        {
            type: 'counter',
            slug: 'charging_batteries_count',
            prevent_hidden: false,
        },
    ],
    fields_order: [
        'transmitters_count',
        'headphones',
        'microphones_count',
        'charging_batteries',
        'charging_batteries_count',
    ],
    images: [
        {
            counter_from: 'microphones_count',
            success_icon: false,
            hide_count: false,
            align: 'row',
            size: 'small',
            images: [
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-transmitter.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-transmitter.webp',
                    values: {
                        transmitters_count: 'all',
                    },
                },
            ],
        },
        {
            counter_from: 'transmitters_count',
            success_icon: false,
            hide_count: false,
            align: 'row',
            size: 'small',
            images: [
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-black.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-black.webp',
                    values: {
                        headphones: 'without-headphones',
                    },
                },
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-black-base.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-black-base.webp',
                    values: {
                        headphones: 'p303',
                    },
                },
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-black-comfort.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-black-comfort.webp',
                    values: {
                        headphones: 'p101',
                    },
                },
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-black-disposable.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-black-disposable.webp',
                    values: {
                        headphones: 'de300',
                    },
                },
                {
                    image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-black-stereo.png',
                    image_webp:
                        '/assets/images/products/radioguide/webp/reinvox-duo-black-stereo.webp',
                    values: {
                        headphones: 'p202',
                    },
                },
            ],
        },
        {
            counter_from: 'charging_batteries_count',
            success_icon: false,
            hide_count: false,
            align: 'row',
            size: 'big',
            images: [
                {
                    image: '/assets/images/products/charging/jpg-png/radioguide-15-cells.png',
                    image_webp:
                        '/assets/images/products/charging/webp/radioguide-15-cells.webp',
                    values: {
                        charging_batteries: 'c215pr',
                    },
                },
                {
                    image: '/assets/images/products/charging/jpg-png/radioguide-25-cells.png',
                    image_webp:
                        '/assets/images/products/charging/webp/radioguide-25-cells.webp',
                    values: {
                        charging_batteries: 'c225pr',
                    },
                },
            ],
        },
    ],
    price_modificators: {
        headphones: {
            de300: 1000,
        },
    },
    prices: {},
}

export const radioguideFieldTitles: IFieldTitlesBySlug = {
    headphones: 'Наушники для приемников',
    charging_batteries: 'Зарядка батарей',
}

export const radioguideUpdaters: ICalculatorSettingUpdaters = {
    charging_batteries: updateRadioguideBatteriesCount,
    transmitters_count: updateRadioguideBatteriesCount,
    microphones_count: updateRadioguideBatteriesCount,
}

function updateRadioguideBatteriesCount(
    selectedValues: ICalculatorSelectedValues
): [string, string | number, boolean, boolean][] {
    if (selectedValues.charging_batteries === 'no-charging-station') {
        return [['charging_batteries_count', 0, true, false]]
    }

    let value = 1

    if (
        'transmitters_count' in selectedValues &&
        'microphones_count' in selectedValues
    ) {
        const cells: 25 | 15 =
            selectedValues.charging_batteries === 'c225pr' ? 25 : 15
        value = Math.round(
            (Number(selectedValues.transmitters_count) +
                Number(selectedValues.microphones_count)) /
                cells
        )
    }

    return [['charging_batteries_count', value > 0 ? value : 1, false, false]]
}

export function radioguideGetPrice(
    calculator: ICalculatorSetting,
    selectedValues: ICalculatorSelectedValues
): string {
    const productPrices: { [key: string]: number } = getCalculatorProductsPrice(
        calculator,
        selectedValues
    )

    const transmittersPrice: number =
        (productPrices['r200pr'] || 0) *
        Number(selectedValues.transmitters_count)

    const headphonesPrice: number =
        (productPrices['headphones'] || 0) *
        (calculator.price_modificators['headphones']?.[
            selectedValues['headphones']
        ]
            ? 1
            : Number(selectedValues.transmitters_count))

    const microphonesPrice: number =
        ((productPrices['m50pr'] || 0) + (productPrices['t208pr'] || 0)) *
        Number(selectedValues.microphones_count)

    const chargingBatteriesPrice: number =
        (productPrices['charging_batteries'] || 0) *
        Number(selectedValues.charging_batteries_count)

    const price: number =
        transmittersPrice +
        headphonesPrice +
        microphonesPrice +
        chargingBatteriesPrice

    return formatPrice(price)
}
